import { Country, PermissionsConstants, ScopeType } from 'src/common/types';
import { formatMessage } from 'src/core/services/http.service';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import { RoleWithExpirationDate } from './role-assignments-store';
import FileSaver from 'file-saver';
import i18n, { Languages } from 'src/i18n';
import i18next from 'src/i18n';
import { TFunction } from 'i18next';
import moment, { Moment } from 'moment';
import { AxiosError } from 'axios';

export const ORGANIZATIONS_MEMBER_LIMIT = 70;

export interface OrganizationMemberItem {
  userId: string;
  displayName: string;
  email: string;
  roles: RoleWithExpirationDate[];
  resendInvitation: boolean;
}

export enum OrganizationCreationStatus {
  Pending = 'Pending',
  Created = 'Created',
  Provisioned = 'Provisioned',
  Available = 'Available',
  Error = 'Error',
}

export interface OrganizationItem {
  isExporting: false;
  id: string;
  displayName: string;
  email: string;
  address: string;
  subscriptionId: string;
  createdOn: string;
  createdById: string;
  members: OrganizationMemberItem[];
  parentOrganizationId?: string;
  parentOrganizationDisplayName?: string;
  isPartner: boolean;
  isBlocked: boolean;
  defaultLanguage: string;
  creationStatus: OrganizationCreationStatus;
  creationStatusDescription: string;
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
  twoFactorEnabled?: boolean;
}

export interface OrganizationSummary {
  id: string;
  displayName: string;
  email: string;
  address: string;
  subscriptionId: string;
  createdOn: string;
  createdById: string;
  parentOrganizationId?: string;
  parentOrganizationDisplayName?: string;
  isPartner: boolean;
  isBlocked: boolean;
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
}

export interface CreateOrganizationUser {
  userIdOrEmail: string;
  defaultRoleIds: any[];
}

export interface CreateOrganizationItem {
  displayName: string;
  email: string;
  address: string;
  isPartner: boolean;
  defaultLanguage: string;
  ownerUserIdOrEmail: string;
  initialUsers: CreateOrganizationUser[];
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
  ownerRolesExpirationDate: Date;
}

export interface FixScopeSyncIssuesItemResult {
  scopeId: string;
  scopeName: string;
  scopeType: ScopeType;
  parentId: string;
  parentName: string;
  parentType: ScopeType;

  fixed: boolean;
  problem: string;
}

export interface CsvReportRequest {
  csvSeparator: string;
  headers: reportHeader[];
  currentLanguage: string;
  fileName: string;
  items: OrganizationDatabaseStatusItem[];
}

export interface PdfReportRequest {
  headers: reportHeader[];
  currentLanguage: string;
  fileName: string;
  items: OrganizationDatabaseStatusItem[];
}

export interface reportHeader {
  key: CsvKeys;
  translatedHeader: string;
}

export interface OrganizationDatabaseStatusItem {
  organizationName: string;
  result: string;
}

export interface CheckDatabaseLog {
  id?: string;
  createdOn?: Date;
  createdBy?: string;
  userName?: string;
  checkDbLogResults?: CheckDatabaseLogResultItem[];
  success: boolean;
}

export interface DatabaseInconsistencyLog {
  id: string;
  organizationName: string;
  organizationId: string;
  createdOn: Date;
  createdBy: string;
  userName: string;
  items: number;
}

export interface DatabaseInconsistencyLogResultItem {
  scopeName: string;
  scopeType: string;
  parentName: string;
  parentType: string;
  problem: string;
  fixed: boolean;
}

export enum ExportImportLogType {
  BackupSuccess = 'BackupSuccess',
  BackupError = 'BackupError',
  RestoreBackupStart = 'RestoreBackupStart',
  BackupStart = 'BackupStart',
  RestoreBackupSuccess = 'RestoreBackupSuccess',
  RestoreBackupError = 'RestoreBackupError',
}

export interface CheckDatabaseLogResultItem {
  checkDbLogId?: string;
  organizationId: string;
  organizationName: string;
  success: boolean;
  errorMessage: string;
}

export interface OrganizationBackupItem {
  organizationName: string;
  organizationId: string;
  backupId: string;
  createOn: Date;
}

export enum CsvKeys {
  OrganizationName,
  Status,
}

export enum OrganizationDbLogsExportOption {
  CSV = 'CSV',
  PDF = 'PDF',
}

export interface PdfReportCheckDbResultRequest {
  createdOn: Date;
  createByUserName: string;
  currentLanguage: string;
  success: boolean;
  items: OrganizationDatabaseStatusItem[];
}

export const getDefaultOrganizationTestingEndDate = (min?: Moment) => {
  const endDate = min ? min.toDate() : new Date();
  endDate.setTime(endDate.getTime() + 24 * 36e5);
  return moment(endDate);
};

export const getTranslatedHeader = (key: CsvKeys, t: TFunction) => {
  switch (key) {
    case CsvKeys.OrganizationName:
      return t('OrganizationName');
    case CsvKeys.Status:
      return t('Status');
  }
};

export const getOrganizationIdConstraints = (ids: string[]) => {
  let idConstraints = '';

  for (const [index, id] of ids.entries()) {
    idConstraints += `ids=${id}`;
    if (index < ids.length - 1) {
      idConstraints += '&';
    }
  }

  return idConstraints;
};

export class OrganizationBackupStore extends DataStore<OrganizationBackupItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/export-import/api/backup`, []);
  }

  public async loadSelected(query: Partial<Query>, selectedIds: string[]) {
    let idConstraints = getOrganizationIdConstraints(selectedIds);

    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationBackupItem>>(
        `${this.baseUrl}/azure-backups?${DataStore.buildUrl(query as Query)}&${idConstraints}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async load(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationBackupItem>>(
        `${this.baseUrl}/azure-backups?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items;
        s.count = response.data.count;
        return s;
      });
      return true;
    });
  }

  public async getByOrganizationId(organizationId: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<OrganizationBackupItem[]>(`${this.baseUrl}/azure-backups/${organizationId}`);
        return response.data;
      })) ?? []
    );
  }

  public async restoreOrganization(backupId: string, organizationId: string) {
    const item = { tenantId: organizationId, backupId };
    return await this.handleCallAsync(async () => {
      try {
        await this.httpService.post<any, any>(`${this.baseUrl}/restore-from-azure`, item);
        return { isSuccess: true };
      } catch (ex) {
        const error = ex as AxiosError<{ detail: string }>;
        return { isSuccess: false, error: error.response?.data.detail };
      }
    });
  }

  public async generateDownloadUrl(backupId: string, organizationId: string) {
    try {
      const response = await this.httpService.get<{ status: string; token: string }>(
        `${this.baseUrl}/azure-backup-token/${organizationId}/${backupId}`
      );
      const url = response.data.token;
      return { isSuccess: true, url };
    } catch (e) {
      return { isSuccess: false, url: '' };
    }
  }
}
export class OrganizationDatabaseLogsStore extends DataStore<CheckDatabaseLog> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/check-db-log`, []);
  }

  public async registerLog(item: CheckDatabaseLog) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CheckDatabaseLog, CommandResult<CheckDatabaseLog>>(`${this.baseUrl}`, item);
      return result.data;
    });
  }
}

export class OrganizationDatabaseLogsResultStore extends DataStore<CheckDatabaseLogResultItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/check-db-log`, []);
  }

  public async getLogData(id: string) {
    const query = {
      filter: [`id eq ${id}`],
    };
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<CheckDatabaseLog>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
      return response.data.items[0];
    });
  }

  public async loadById(id: string, query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<CheckDatabaseLogResultItem>>(
        `${this.baseUrl}/results/${id}?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }
}

export class OrganizationDatabaseInconsistencyLogStore extends DataStore<DatabaseInconsistencyLog> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/scope-sync-issues-logs`, []);
  }
}

export class OrganizationDatabaseInconsistencyLogResultStore extends DataStore<DatabaseInconsistencyLogResultItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/scope-sync-issues-logs`, []);
  }

  public async loadById(id: string, query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<DatabaseInconsistencyLogResultItem>>(
        `${this.baseUrl}/results/${id}?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }
}

export class OrganizationSummaryStore extends DataStore<OrganizationSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations`, []);
  }

  public async delete(id: string, params?: any) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.delete<any, CommandResult<OrganizationSummary>>(`${this.baseUrl}/${encodeURIComponent(id)}`, params);
      return response.data;
    });
  }

  public async getItems(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
      return response.data.items || [];
    });
  }

  public async getUserPartnerOrganizations(permission: PermissionsConstants, query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationSummary>>(
        `${this.baseUrl}/with-permission?${DataStore.buildUrl(query as Query)}`,
        {
          permission,
        }
      );
      return response.data.items || [];
    });
  }

  public async organizationExists(id: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<QueryResult<OrganizationSummary>>(`${this.baseUrl}/${id}`);
        if (response.data) {
          return true;
        }
        return false;
      })) ?? false
    );
  }
}

export class OrganizationItemStore extends FormStore<OrganizationItem, CreateOrganizationItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations`);
  }

  OrganizationMembersBasePath = 'users';
  RestartOrganizationCreationFlowBasePath = 'restart-creation-flow';
  ChangeBlockStateBasePath = 'change-block-state';
  sendInvitationPath = 'send-invitation';
  checkOrganizationDatabasePath = 'check-db';
  fixScopesSyncIssuesPath = 'fix-scopes-sync-issues';

  public async create(item: CreateOrganizationItem) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CreateOrganizationItem, CommandResult<OrganizationItem>>(`${this.baseUrl}`, item);
      return result.data;
    });
  }

  public async switchMFA(id: string, newValue: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ twoFactorEnabled: boolean }, CommandResult<{ twoFactorEnabled: boolean }>>(
        `${this.baseUrl}/${id}/two-factor`,
        { twoFactorEnabled: newValue }
      );
      return result.data;
    });
  }

  public async changeParent(id: string, newParent: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ parentOrganizationId: string }, CommandResult<{ parentOrganizationId: boolean }>>(
        `${this.baseUrl}/${id}/change-parent`,
        { parentOrganizationId: newParent }
      );
      return result.data;
    });
  }

  public async changePartnerStatus(id: string, isPartner: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ isPartner: boolean }, CommandResult<{ parentOrganizationId: boolean }>>(
        `${this.baseUrl}/${id}/change-partner-status`,
        { isPartner }
      );
      return result.data;
    });
  }

  public async getOrganizationData(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<OrganizationItem>(`${this.baseUrl}/${encodeURIComponent(id)}`);
      return response.data;
    });
  }

  public async fixScopesSyncIssues(orgId: string, justCheck = false, scopeType: ScopeType | null = null) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ justCheck: boolean; scopeType: ScopeType | null }, CommandResult<FixScopeSyncIssuesItemResult[]>>(
        `${this.baseUrl}/${encodeURIComponent(orgId)}/${this.fixScopesSyncIssuesPath}`,
        { justCheck, scopeType },
        { timeout: 1000 * 60 * 10 }
      );
      return result.data;
    });
  }

  public async restartOrganizationCreationFlow(id: string) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<{}, CommandResult<OrganizationItem>>(
          `${this.baseUrl}/${encodeURIComponent(id)}/${this.RestartOrganizationCreationFlowBasePath}`,
          {}
        );
        return result.data;
      } catch (ex) {
        return { isSuccess: false, message: formatMessage(ex) };
      }
    });
  }

  public async checkOrganizationDatabase(id: string) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<{}, CommandResult<{}>>(
          `${this.baseUrl}/${encodeURIComponent(id)}/${this.checkOrganizationDatabasePath}`,
          {}
        );
        return result.data;
      } catch (ex) {
        return { isSuccess: false, message: formatMessage(ex) };
      }
    });
  }

  public async checkAllOrganizationsDatabases() {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<{}, CommandResult<{}> & { message: string }>(
          `${this.baseUrl}/check-db-all`,
          {},
          { timeout: 1000 * 60 * 5 }
        );
        return result.data;
      } catch (ex) {
        return { isSuccess: false, message: formatMessage(ex) };
      }
    });
  }

  public async getCheckOrganizationsReportCSV(items: OrganizationDatabaseStatusItem[], headers: reportHeader[]) {
    const request: CsvReportRequest = {
      headers,
      csvSeparator: ',',
      currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
      fileName: `database_checks.csv`,
      items: items,
    };

    return await this.handleCallAsync(async () => {
      const result = await this.httpService.post<any, Blob>(`${this.baseUrl}/export-check-db-result`, request, { responseType: 'blob' });
      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }
      FileSaver.saveAs(result.data, 'database_checks.csv');
      return true;
    });
  }

  public async getCheckOrganizationsReportPDF(items: OrganizationDatabaseStatusItem[], userName: string, createdOn: Date, isSuccess: boolean) {
    const request: PdfReportCheckDbResultRequest = {
      createByUserName: userName,
      createdOn: createdOn,
      currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
      items: items,
      success: isSuccess,
    };

    return await this.handleCallAsync(async () => {
      const result = await this.httpService.post<any, Blob>(`${this.baseUrl}/export-pdf-check-db-result`, request, { responseType: 'blob' });
      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }
      FileSaver.saveAs(result.data, 'database_checks.pdf');
      return true;
    });
  }

  public async addOrganizationMembers(id: string, userIdOrEmails: OrganizationMemberItem[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ members: OrganizationMemberItem[] }, CommandResult<{ members: OrganizationMemberItem[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.OrganizationMembersBasePath}`,
        {
          members: userIdOrEmails,
        }
      );
      return result.data;
    });
  }

  public async deleteOrganizationMembers(id: string, userIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{ members: string[] }, CommandResult<{ members: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.OrganizationMembersBasePath}`,
        {
          members: userIds,
        }
      );
      return result.data;
    });
  }

  public async changeOrganizationBlockStatus(id: string, isBlocked: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ isBlocked: boolean }, CommandResult<{ isBlocked: boolean }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.ChangeBlockStateBasePath}`,
        { isBlocked }
      );
      return result.data;
    });
  }

  public async sendInvitation(id: string, userId: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{}, CommandResult<{}>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.sendInvitationPath}/${encodeURIComponent(userId)}`,
        {}
      );
      return result.data;
    });
  }

  public async checkLockedStatus(id: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<boolean>(`${this.baseUrl}/check-locked-status/${id}`);
        return response.data;
      })) ?? false
    );
  }

  public async checkExportingStatus(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<CheckDatabaseLogResultItem>>(`${this.baseUrl}/check-exporting-status/${id}`);
      return response.data;
    });
  }
}

export class OrganizationImportStore extends FormStore<any, any> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/export-import/api/backup`);
  }

  public async uploadOrganizationBackup(file: File) {
    return await this.handleCallAsync(async () => {
      try {
        var formData = new FormData();
        formData.append('file', file);
        var result = await this.httpService.post<FormData, any>(`${this.baseUrl}/restore`, formData, { timeout: 1000 * 60 * 10 });
        if (result.status === 200) {
          return { isSuccess: true };
        }
      } catch (error) {
        const err = error as AxiosError<{ detail: string }>;
        const errorMessage = err?.response?.data.detail;
        return { isSuccess: false, error: errorMessage };
      }
    });
  }

  public async exportFile(organizationId: string, fileName: string) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<any, { error: string }>(`${this.baseUrl}/${organizationId}`, {});
        if (result.status === 200) {
          return { isSuccess: true };
        }
      } catch (error) {
        const err = error as AxiosError<{ detail: string }>;
        const errorMessage = err?.response?.data.detail;
        return { isSuccess: false, error: errorMessage };
      }
    });
  }
}
